import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { FaFileAlt, FaUpload, FaTrash } from 'react-icons/fa';
import { DocumentUploadModal } from '../DocumentUploadModal';

/**
 * The record-specific calls a document attachment needs. Each record type (action item, project item,
 * meeting note, daily update) supplies its own adapter; the attachment UI and behaviour stay the same.
 */
export interface DocumentAdapter {
  /** Uploads a file to the record and returns the stored document (must include `url`). */
  upload: (token: string, file: File) => Promise<any>;
  /** Removes the record's document. */
  remove: (token: string) => Promise<unknown>;
  /** Builds the download URL used when the record has no stored URL. Optional: without it, a missing URL is an error. */
  downloadUrl?: (fileName: string) => string;
}

export interface DocumentAttachmentProps {
  /** Human-readable record name used in messages, e.g. "action item". */
  recordLabel: string;
  /** Stable identifier for the record, used to build a placeholder document id. */
  recordKey: string;
  documentUrl?: string | null;
  fileName?: string | null;
  adapter: DocumentAdapter;
  onDocumentChange?: (documentUrl: string | undefined, fileName: string | undefined) => void;
}

interface DocumentData {
  id: string;
  name: string;
  file_name?: string;
  size: number;
  mimetype?: string;
  uploadedBy: string;
  uploadedAt: string;
  url?: string;
}

const capitalizeWords = (text: string): string =>
  text.replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Shows a record's document as a download link with a delete button, or an Upload button when there is none.
 * The document comes from the record's props (the list response); no request is made to look it up.
 */
export const DocumentAttachment = ({ recordLabel, recordKey, documentUrl, fileName, adapter, onDocumentChange }: DocumentAttachmentProps) => {
  const { keycloak: { token } } = useKeycloak();
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  // Set after a local upload or delete, so a stale prop does not overwrite the result.
  const [hasLocalChange, setHasLocalChange] = useState(false);

  useEffect(() => {
    setHasLocalChange(false);
  }, [recordKey]);

  useEffect(() => {
    if (hasLocalChange) return;

    if (documentUrl && fileName) {
      setDocumentData({
        id: recordKey,
        name: fileName,
        file_name: fileName,
        size: 0,
        mimetype: 'application/octet-stream',
        uploadedBy: 'unknown',
        uploadedAt: new Date().toISOString(),
        url: documentUrl,
      });
      return;
    }

    // The record's list response already says whether it has a document, so no document needs no request.
    setDocumentData(null);
  }, [documentUrl, fileName, hasLocalChange, recordKey]);

  const handleUploadFromModal = async (file: File) => {
    if (!token) throw new Error('No authentication token');

    try {
      const response = await adapter.upload(token, file);

      const normalizedDoc: DocumentData = {
        id: response.id || recordKey,
        name: response.name || file.name,
        file_name: response.name || file.name,
        size: response.size || file.size,
        mimetype: response.mimetype || file.type,
        uploadedBy: response.uploadedBy || 'current-user',
        uploadedAt: response.uploadedAt || new Date().toISOString(),
        url: response.url,
      };

      setDocumentData(normalizedDoc);
      setHasLocalChange(true);

      if (onDocumentChange) {
        onDocumentChange(normalizedDoc.url, normalizedDoc.file_name);
      }

      toast.success(`Document "${file.name}" uploaded successfully`);
      return response;
    } catch (error) {
      console.error(`Failed to upload ${recordLabel} document:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('413') || errorMessage.includes('Content Too Large')) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB. Please try a smaller file.`);
      } else if (errorMessage.includes('415') || errorMessage.includes('Unsupported Media Type')) {
        toast.error('File type not supported. Please try a different file format.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error(`You do not have permission to upload documents to this ${recordLabel}.`);
      } else if (errorMessage.includes('404')) {
        toast.error(`${capitalizeWords(recordLabel)} not found. Please refresh the page and try again.`);
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(`Failed to upload document: ${errorMessage}`);
      }

      // Re-thrown so DocumentUploadModal can show its own state.
      throw error;
    }
  };

  const handleDownload = async () => {
    if (!documentData || !token) return;

    try {
      let downloadUrl = documentData.url;
      if (!downloadUrl) {
        const name = documentData.file_name || documentData.name;
        if (!name || !adapter.downloadUrl) throw new Error('No download URL or filename available');
        downloadUrl = adapter.downloadUrl(name);
      }

      // Encode non-ASCII characters (e.g. Cyrillic filenames) in the URL path
      try {
        const urlObj = new URL(downloadUrl);
        urlObj.pathname = urlObj.pathname.split('/').map((seg: string) => encodeURIComponent(decodeURIComponent(seg))).join('/');
        downloadUrl = urlObj.href;
      } catch (_) { /* keep original URL */ }

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: { 'authorization': 'Bearer ' + token },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = documentData.file_name || documentData.name || 'document';

      const openableTypes = ['pdf', 'image', 'text'];
      const canOpen = documentData.mimetype
        ? openableTypes.some((type) => documentData.mimetype?.toLowerCase().includes(type))
        : false;

      if (canOpen) {
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.success('Document opened successfully');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Document downloaded successfully');
      }
    } catch (error) {
      console.error(`Failed to download ${recordLabel} document:`, error);
      toast.error(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async () => {
    if (!documentData || !token) return;
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await adapter.remove(token);
      setDocumentData(null);
      setHasLocalChange(true);

      if (onDocumentChange) {
        onDocumentChange(undefined, undefined);
      }

      toast.success('Document deleted successfully');
    } catch (error) {
      console.error(`Failed to delete ${recordLabel} document:`, error);
      toast.error(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="flex items-center space-x-1">
      {documentData ? (
        <div className="flex flex-col space-y-1">
          <div className="flex items-center space-x-2 bg-[#fafafa] border border-[#eaeaea] rounded-lg px-2 py-1">
            <button
              data-testid="document-download-button"
              onClick={handleDownload}
              className="cursor-pointer flex items-center space-x-1 text-xs text-[#555] hover:text-[#171717]"
              title={`${documentData.name}${documentData.size ? ` (${formatFileSize(documentData.size)})` : ''}`}
            >
              <FaFileAlt className="text-xs" />
              <span className="font-medium" title={documentData.file_name || documentData.name}>
                {(() => {
                  const displayName = documentData.file_name || documentData.name || 'document';
                  return displayName.length > 7 ? `${displayName.substring(0, 7)}...` : displayName;
                })()}
              </span>
            </button>
            <button
              data-testid="document-delete-button"
              onClick={handleDelete}
              className="text-xs text-[#aaa] hover:text-red-500 p-1"
              title="Delete document"
            >
              <FaTrash />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center">
          <button
            data-testid="document-upload-button"
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center space-x-1 bg-[#fafafa] hover:bg-[#fafafa] border border-[#eaeaea] rounded-lg px-3 py-1 text-xs text-[#888] hover:text-[#444] transition-colors"
            title="Upload document"
          >
            <FaUpload className="text-xs" />
            <span>Upload</span>
          </button>
        </div>
      )}

      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFromModal}
        showSuccessToast={false}
        title={`Upload ${capitalizeWords(recordLabel)} Document`}
        maxFileSize={10}
        description="Select a document or drag and drop it here"
      />
    </div>
  );
};
